import { z } from "zod";

/** Valid subject options for the contact form */
const SUBJECT_OPTIONS = [
  "General Question",
  "Booking Support",
  "Listing Help",
  "Payment Issue",
  "Safety Concern",
  "Technical Issue",
  "Partnership Inquiry",
  "Feedback & Suggestions",
  "Other",
] as const;

/** Regex for name: letters, spaces, hyphens, apostrophes */
const NAME_REGEX = /^[A-Za-z\s'-]+$/;

/**
 * Shared schema for contact form fields.
 * Used for both client-side (react-hook-form) and server-side validation.
 */
export const contactFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(50, "First name must be 50 characters or less")
    .regex(NAME_REGEX, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(50, "Last name must be 50 characters or less")
    .regex(NAME_REGEX, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(100, "Email must be 100 characters or less"),
  subject: z
    .string()
    .min(1, "Please select a subject")
    .refine((val) => SUBJECT_OPTIONS.includes(val as (typeof SUBJECT_OPTIONS)[number]), {
      message: "Please select a valid subject",
    }),
  message: z
    .string()
    .min(1, "Message is required")
    .max(1000, "Message must be 1000 characters or less"),
});

/**
 * Full submission schema including security fields.
 * Used for server-side validation only.
 */
export const contactFormSubmissionSchema = contactFormSchema.extend({
  timestamp: z.string().min(1, "Invalid submission timestamp"),
  jsEnabled: z.literal("true", {
    errorMap: () => ({ message: "JavaScript must be enabled" }),
  }),
  captchaToken: z.string().min(1, "reCAPTCHA verification required"),
  website: z.string().refine((v) => !v || v.length === 0, { message: "Invalid submission" }).optional().default(""),
  confirm_email: z.string().refine((v) => !v || v.length === 0, { message: "Invalid submission" }).optional().default(""),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;
export type ContactFormSubmission = z.infer<typeof contactFormSubmissionSchema>;
